import { Map } from 'immutable';
import { EditorState, Modifier, ContentBlock, SelectionState, ContentState } from 'draft-js';

import { BlockInfo, WordInfo } from './models';

enum EntityType {
  SYNC_WORD = 'SYNC_WORD',
  SYNC_SEGMENT = 'SYNC_SEGMENT',
}

export const getBlockEntity = (block: ContentBlock): string => {
  const entities: string[] = [];
  block.findEntityRanges(
    (character) => {
      if (character.getEntity() !== null) {
        entities.push(character.getEntity());
        return true;
      }
      return false;
    },
    (start, end) => {},
  );
  if (entities.length > 1) {
    console.debug('More than 1 entity in block: ', block.getKey());
  }
  return entities[0];
};

export const findTimestamps = (
  content: ContentState,
  selection: SelectionState,
  after?: boolean,
): {
  startTime: number;
  endTime: number;
} => {
  const { anchorKey } = selection.toObject();

  // Use start and end time from current block
  const currentBlock = content.getBlockForKey(anchorKey);
  const data = currentBlock.getData().toObject() as BlockInfo;
  if (data.startTime && data.endTime) {
    return { ...data };
  }

  const block = !after ? content.getBlockBefore(anchorKey) : content.getBlockAfter(anchorKey);

  if (!block) {
    if (!after) {
      return findTimestamps(content, selection, true);
    }
  } else {
    const entityKey = getBlockEntity(block);
    if (entityKey) {
      const { data } = content.getEntity(entityKey).getData() as { data: WordInfo[] };
      if (data.length > 0) {
        const { startTime, endTime } = after ? data[0] : data[data.length - 1];
        return { startTime, endTime };
      }
    }
  }

  return {
    startTime: 0,
    endTime: 0,
  };
};

const getBlockEntities = (block: ContentBlock): string[] => {
  const entities: string[] = [];
  block.findEntityRanges(
    (character) => {
      if (character.getEntity() !== null) {
        entities.push(character.getEntity());
        return true;
      }
      return false;
    },
    (start, end) => {},
  );

  if (entities.length > 1) {
    console.debug('> 1 entities in block: ', block.getKey());
    console.debug(entities);
  }

  return entities;
};

const getWordData = (contentState: ContentState, entityKey: string): WordInfo[] => {
  const entity = contentState.getEntity(entityKey);
  const { data } = entity.getData() as { data: WordInfo[] };
  return data;
};

const getWordIndexAndOffset = (wordOffset: number, data: WordInfo[]): { index: number; offset: number } => {
  let currentOffset = 0;
  let offset = 0;
  let index = 0;
  data.some(({ word }: WordInfo, i: number) => {
    if (currentOffset + word.length >= wordOffset) {
      offset = wordOffset - currentOffset;
      index = i;
      return true;
    } else {
      currentOffset += word.length;
      return false;
    }
  });

  return { index, offset };
};

export const logBlocks = (contentState: ContentState, limit?: number) => {
  contentState.getBlocksAsArray().forEach((block, i) => {
    if (!limit || i < limit) {
      console.debug(block.toObject());
      const entities = getBlockEntities(block);
      if (entities.length === 0) {
        console.debug('No entities in block: ', block.getKey());
      } else {
        entities.forEach((entityKey) => {
          const entity = contentState.getEntity(entityKey);
          const { data } = entity.getData() as { data: WordInfo[] };
          console.debug(data.map((wordInfo) => wordInfo.word).join(' '));
        });
      }
    }
  });
};

const createEntityAtSelection = (
  contentState: ContentState,
  data: WordInfo[],
  selection?: SelectionState,
): ContentState => {
  let newContentState = contentState.createEntity(EntityType.SYNC_SEGMENT, 'MUTABLE', { data });

  newContentState = Modifier.replaceText(
    newContentState,
    selection || contentState.getSelectionAfter(),
    data.map((wordInfo) => wordInfo.word).join(''),
    undefined,
    newContentState.getLastCreatedEntityKey(),
  );

  return newContentState;
};

// const getFirstUnmarkedBlock = (
//     contentState: ContentState,
//     key: string,
// ): string => {
//     const blockBefore = contentState.getBlockBefore(key);
//     if (blockBefore) {
//         const { speakerId } = blockBefore.getData().toObject();
//         if (!speakerId) {
//             return getFirstUnmarkedBlock(contentState, blockBefore.getKey());
//         } else {
//             return key;
//         }
//     } else {
//         return key;
//     }
// }

export const mergeBlockDataForSelection = (
  contentState: ContentState,
  selection: SelectionState,
  data: { [key: string]: any },
): ContentState => {
  return Modifier.mergeBlockData(contentState, selection, Map({ ...data }));
};

export const mergeBlockDataForKey = (
  contentState: ContentState,
  key: string,
  data: { [key: string]: any },
): ContentState => {
  const selection = new SelectionState({
    anchorKey: key,
    focusKey: key,
  });
  return Modifier.mergeBlockData(contentState, selection, Map({ ...data }));
};

export const mergeSegments = (
  editorState: EditorState,
  selection?: SelectionState,
): { content: ContentState; selection: SelectionState } => {
  const sel = selection || editorState.getSelection();
  const content = editorState.getCurrentContent();

  const { anchorKey, focusKey, isBackward } = sel.toObject();

  const startKey = isBackward ? focusKey : anchorKey;
  const endKey = isBackward ? anchorKey : focusKey;

  const blocks = content.getBlocksAsArray();

  const { blockData, data } = blocks.reduce(
    (
      acc: {
        blockData: Partial<BlockInfo>;
        data: WordInfo[];
        done: boolean;
      },
      currentBlock: ContentBlock,
    ) => {
      const key = currentBlock.getKey();

      const addBlock = () => {
        const entityKey = getBlockEntity(currentBlock);
        const newData = getWordData(content, entityKey);
        let sId;
        if (!acc.blockData.speakerId) {
          const { speakerId } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
          sId = speakerId;
        }
        return {
          blockData: {
            ...acc.blockData,
            speakerId: sId || acc.blockData.speakerId,
          },
          data: acc.data.concat(newData),
          done: acc.done,
        };
      };

      if (key === startKey) {
        const newAcc = addBlock();
        const { startTime, speakerId } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
        return {
          ...newAcc,
          blockData: {
            ...newAcc.blockData,
            startTime,
            speakerId,
          },
          done: startKey === endKey,
        };
      } else if (key === endKey) {
        const newAcc = addBlock();
        const { endTime } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
        return {
          ...newAcc,
          blockData: {
            ...acc.blockData,
            endTime,
          },
          done: true,
        };
      } else if (acc.data.length !== 0 && !acc.done) {
        return addBlock();
      } else {
        return acc;
      }
    },
    { blockData: {}, data: [], done: false },
  );

  const allEntitiesSelection = sel.merge({
    anchorOffset: isBackward ? content.getBlockForKey(anchorKey).getText().length : 0,
    focusOffset: isBackward ? 0 : content.getBlockForKey(focusKey).getText().length,
  });

  let newContentState: ContentState;
  newContentState = Modifier.removeRange(content, allEntitiesSelection, 'forward');
  newContentState = createEntityAtSelection(newContentState, data, newContentState.getSelectionAfter());

  const newSelection = newContentState.getSelectionAfter().merge({
    anchorOffset: 0,
  });

  newContentState = mergeBlockDataForSelection(newContentState, newSelection, { ...blockData });

  return { content: newContentState, selection: newSelection };
};

const getNewBlockData = (
  content: ContentState,
  blockKey: string,
  startOffset: number,
  endOffset: number,
): WordInfo[] => {
  const block = content.getBlockForKey(blockKey);
  const entityKey = getBlockEntity(block);
  if (!entityKey) {
    return [];
  }

  const data = getWordData(content, entityKey);
  const startWord = getWordIndexAndOffset(startOffset, data);
  const endWord = getWordIndexAndOffset(endOffset, data);

  const newData = data
    .map((wordInfo, i) => {
      if (i === startWord.index) {
        const word = data[startWord.index];
        return {
          ...word,
          word: word.word.slice(startWord.offset, endOffset),
        };
      } else if (i === endWord.index) {
        const word = data[endWord.index];
        return {
          ...word,
          word: word.word.slice(0, endWord.offset),
        };
      } else {
        return wordInfo;
      }
    })
    .filter((wordInfo, i) => i >= startWord.index && i <= endWord.index && wordInfo.word !== '');

  return newData;
};

const confirmSpaceIfEmptyNewSegment = (newStartData: WordInfo[], newEndData: WordInfo[], firstEndTime: number) => {
  /** Here we check if either of new segments would end up with no charaters.
   *  We adress it by adding a space if there isn't one.
   *  Before this change trying to split empty segments resulted in crashing.
   */

  if (newEndData.length < 1) {
    newEndData.push({
      word: ' ',
      startTime: firstEndTime,
      endTime: firstEndTime,
    });
  }

  if (newStartData.length < 1) {
    newStartData.push({
      word: ' ',
      startTime: firstEndTime,
      endTime: firstEndTime,
    });
  }
};

export const wordInfosFromText = (text: string, timestamp: number): WordInfo[] => {
  const newText = text.replace('\n', '');
  return newText.split(' ').map((word: string) => ({
    word: (word += ' '),
    startTime: timestamp,
    endTime: timestamp,
  }));
};

export const splitSegments = (editorState: EditorState): EditorState => {
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();

  const blockKey = selection.getStartKey();
  const block = contentState.getBlockForKey(blockKey);
  const { speakerId, startTime, endTime } = block.getData().toObject() as BlockInfo;

  const offset = selection.getStartOffset();
  const endOffset = block.getText().length;

  const newStartData = getNewBlockData(contentState, blockKey, 0, offset);
  const newEndData = getNewBlockData(contentState, blockKey, offset, endOffset);

  const firstEndTime = newStartData.length > 0 ? newStartData[newStartData.length - 1].endTime : startTime;
  let secondStartTime = newEndData.length > 0 ? newEndData[0].startTime : endTime;

  confirmSpaceIfEmptyNewSegment(newStartData, newEndData, firstEndTime);

  /**
   * If both new segments are empty after split, return old editorState
   * We do not want endless empty segments to exist.
   */
  console.log(newStartData, newEndData);
  if (newStartData.length <= 1 && newEndData.length <= 1) {
    if (newStartData[0].word && newEndData[0].word && newStartData[0].word === ' ' && newEndData[0].word === ' ') {
      return editorState;
    }
  }

  /**
   * If last word in new segment contains a space, start time of next segment is the same as end time
   * of last segment.
   * See if this change holds up.
   */
  if (!newStartData[newStartData.length - 1].word.includes(' ')) {
    secondStartTime = firstEndTime;
  }

  const allBlockSelection = selection.merge({
    anchorOffset: 0,
    focusOffset: block.getText().length,
    isBackward: false,
  });

  let newContentState = Modifier.removeRange(contentState, allBlockSelection, 'forward');

  const firstBlockSelection = newContentState.getSelectionAfter();

  newContentState = Modifier.splitBlock(newContentState, newContentState.getSelectionAfter());
  const secondBlockSelection = newContentState.getSelectionAfter();

  newContentState = mergeBlockDataForSelection(newContentState, firstBlockSelection, {
    startTime,
    endTime: firstEndTime,
    speakerId,
  });

  newContentState = mergeBlockDataForSelection(newContentState, secondBlockSelection, {
    startTime: secondStartTime,
    endTime,
    speakerId,
  });
  newContentState = createEntityAtSelection(newContentState, newStartData, firstBlockSelection);

  newContentState = createEntityAtSelection(newContentState, newEndData, secondBlockSelection);

  let newEditorState = EditorState.push(editorState, newContentState, 'split-block');
  newEditorState = EditorState.acceptSelection(newEditorState, secondBlockSelection);
  newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());
  return newEditorState;
};
